"use client";
import Image from "next/image";
import Link from "next/link";
import styled, {
  CSSProperties,
  RuleSet,
  css,
  keyframes
} from "styled-components";
import { AddIcon } from "../icons/AddIcon";
import { hexToRgb } from "../utils/hexToRgb";
import { defaultTheme } from "./defaultTheme";

export const PageWrapper = styled.div<{
  $padding?: CSSProperties["padding"];
  $justifyContent?: CSSProperties["justifyContent"];
  $gap?: CSSProperties["gap"];
  $flexDirection?: CSSProperties["flexDirection"];
  $position?: CSSProperties["position"];
}>`
  height: 100%;
  width: 100%;
  max-width: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: ${(p) => p.$flexDirection ?? "column"};
  overflow-y: auto;
  overflow-x: hidden;
  ${(p) => (p.$padding ? "padding: " + `${p.$padding};` : "")};
  justify-content: ${(p) => p.$justifyContent};
  gap: ${(p) => p.$gap};
  position: ${(p) => p.$position};
`;

export const screens = ({
  desktop,
  mobile
}: {
  desktop?: RuleSet<object>;
  mobile?: RuleSet<object>;
}) => {
  return css`
    @media screen and (min-width: 600px) {
      ${desktop}
    }

    @media screen and (max-width: 599px) {
      ${mobile}
    }
  `;
};

export const Banner = styled.div<{
  $padding?: CSSProperties["padding"];
  $reverseColors?: boolean;
}>`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: ${(p) => p.$padding ?? " 1rem 1rem"};

  &:nth-child(odd) {
    background-color: ${(p) =>
      p.$reverseColors
        ? defaultTheme.tertiaryColor
        : defaultTheme.secondaryColor};
  }

  &:nth-child(even) {
    background-color: ${(p) =>
      p.$reverseColors
        ? defaultTheme.secondaryColor
        : defaultTheme.tertiaryColor};
  }
`;

export const FlexCoulmn = styled.div<{
  $gap?: CSSProperties["gap"];
  $justifyContent?: CSSProperties["justifyContent"];
  $alignItems?: CSSProperties["alignItems"];
  $width?: CSSProperties["width"];
  $height?: CSSProperties["height"];
  $margin?: CSSProperties["margin"];
  $padding?: CSSProperties["padding"];
  $overflow?: CSSProperties["overflow"];
  $maxWidth?: CSSProperties["maxWidth"];
  $maxHeight?: CSSProperties["maxHeight"];
  $flexWrap?: CSSProperties["flexWrap"];
  $alignSelf?: CSSProperties["alignSelf"];
  $position?: CSSProperties["position"];
}>`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.$gap};
  justify-content: ${(p) => p.$justifyContent};
  align-items: ${(p) => p.$alignItems};
  width: ${(p) => p.$width};
  height: ${(p) => p.$height};
  padding: ${(p) => p.$padding};
  overflow: ${(p) => p.$overflow};
  margin: ${(p) => p.$margin};
  max-width: ${(p) => p.$maxWidth};
  max-height: ${(p) => p.$maxHeight};
  flex-wrap: ${(p) => p.$flexWrap};
  align-self: ${(p) => p.$alignSelf};
  position: ${(p) => p.$position};
`;
export const FlexRow = styled.div<{
  $gap?: CSSProperties["gap"];
  $justifyContent?: CSSProperties["justifyContent"];
  $alignItems?: CSSProperties["alignItems"];
  $width?: CSSProperties["width"];
  $height?: CSSProperties["height"];
  $padding?: CSSProperties["padding"];
  $overflow?: CSSProperties["overflow"];
  $margin?: CSSProperties["margin"];
  $maxWidth?: CSSProperties["maxWidth"];
  $maxHeight?: CSSProperties["maxHeight"];
  $flexWrap?: CSSProperties["flexWrap"];
  $alignSelf?: CSSProperties["alignSelf"];
  $position?: CSSProperties["position"];
  $flexDirection?: "row" | "row-reverse";
}>`
  display: flex;
  flex-direction: row;
  gap: ${(p) => p.$gap};
  justify-content: ${(p) => p.$justifyContent};
  align-items: ${(p) => p.$alignItems};
  width: ${(p) => p.$width};
  height: ${(p) => p.$height};
  padding: ${(p) => p.$padding};
  overflow: ${(p) => p.$overflow};
  margin: ${(p) => p.$margin};
  max-width: ${(p) => p.$maxWidth};
  max-height: ${(p) => p.$maxHeight};
  flex-wrap: ${(p) => p.$flexWrap};
  align-self: ${(p) => p.$alignSelf};
  position: ${(p) => p.$position};
  flex-direction: ${(p) => p.$flexDirection};
`;

export const StyledImage = styled.img<{
  $objectFit?: CSSProperties["objectFit"];
  $maxWidth?: CSSProperties["maxWidth"];
}>`
  border-radius: 10px;
  max-width: ${(p) => p.$maxWidth ?? "90vw"};
  object-fit: ${(p) => p.$objectFit};
`;
export const NextStyledImage = styled(Image)<{
  $objectFit?: CSSProperties["objectFit"];
  $maxWidth?: CSSProperties["maxWidth"];
}>`
  border-radius: 10px;
  max-width: ${(p) => p.$maxWidth ?? "90vw"};
  object-fit: ${(p) => p.$objectFit};
`;

export const sizes = { navbarHeight: 70 } as const;

export const shadow = "box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); ";
export const focusShadow = `box-shadow: 0 0 5px ${defaultTheme.primaryColor};`;

const inputFocusAnimation = keyframes`
  0% {
    transform: scale(1);
    ${shadow}
  }
  50% {
    transform: scale(1.02);
    ${focusShadow}
  }
  100% {
    transform: scale(1);
    ${shadow}
    
  }
`;
export const focusEffect = css`
  &:focus:not(:disabled) {
    outline: none;
    border: 2px solid ${defaultTheme.primaryColor};
    ${shadow}
    animation: ${inputFocusAnimation} 0.5s ease-in-out;
  }
`;
export const Dot = styled.span<{ $color?: string; $size?: number }>`
  width: ${(p) => p.$size ?? 5}px;
  height: ${(p) => p.$size ?? 5}px;
  border-radius: 50%;
  background-color: ${(p) => p.$color ?? defaultTheme.secondaryColor};
`;

export const SvgStyle = css`
  &:hover:not(disabled) {
    cursor: pointer;
    transform: scale(1.1);
  }
`;
export const StyledAddIcon = styled(AddIcon)`
  ${SvgStyle}
`;

const baseFont = css`
  font-family: "Assistant", sans-serif;
`;

export const Fonts = {
  xxSmall: css`
    font-size: 12px;
    ${baseFont}
  `,
  xSmall: css`
    font-size: 14px;
    ${baseFont}
  `,
  small: css`
    font-size: 18px;
    ${baseFont}
  `,
  medium: css`
    font-size: 20px !important;
    ${baseFont}
  `,
  large: css`
    font-size: 24px;
    ${baseFont}
  `,
  xLarge: css`
    font-size: 32px;
    ${baseFont}
  `
};

export const StyledBackdrop = styled.div<{
  $isGlobalSpinner?: boolean;
  $useSecondaryColor?: boolean;
}>`
  height: 100%;
  width: 100%;
  background-color: ${(p) =>
    p.$useSecondaryColor
      ? (() => {
          const rgb = hexToRgb(defaultTheme.secondaryColor);
          return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.9)`;
        })()
      : "rgba(0, 0, 0, 0.5)"};
  position: absolute;
  z-index: 999;
  ${(p) =>
    p.$isGlobalSpinner &&
    css`
      position: fixed;
      z-index: 9999;
    `}
`;

export const CardStyle = styled.div<{
  $isSelected: boolean;
  $selectedColor?: string;
  $fontWeight?: CSSProperties["fontWeight"];
}>`
  user-select: none;
  cursor: pointer;
  border: 1px solid ${defaultTheme.primaryColor};
  background-color: ${(p) =>
    p.$isSelected ? p.$selectedColor ?? defaultTheme.tertiaryColor : "white"};
  padding: 0.5rem;

  ${({ $fontWeight }) =>
    $fontWeight &&
    css`
      font-weight: ${$fontWeight};
    `}
  &:hover {
    background-color: ${(p) => p.$selectedColor ?? defaultTheme.tertiaryColor};
  }

  @media only screen and (min-width: 601px) {
    /* Add your PC-specific styles here */
    &:hover {
      transform: scale(1.03);
    }
  }
`;

export const BaseStyledLink = styled.a`
  padding-bottom: 2px;
  text-align: center;
  font-size: large;
`;
export const NextBaseStyledLink = styled(Link)`
  padding-bottom: 2px;
  text-align: center;
  font-size: large;
`;

export const LinkWithStyle = styled(BaseStyledLink)`
  transition: color 0.3s ease-in-out;
  color: ${defaultTheme.primaryColor};
  &:hover {
    color: ${defaultTheme.secondaryColor};
  }
`;
export const NextLinkWithStyle = styled(NextBaseStyledLink)`
  transition: color 0.3s ease-in-out;
  color: ${defaultTheme.primaryColor};
  &:hover {
    color: ${defaultTheme.secondaryColor};
  }
`;

const ListStyleAnim = keyframes`
0%{
  transform: translateX(100vw);
}
100%{
  /* opacity: 1; */
}
`;
const listStyle = ({ $isInView }: { $isInView?: boolean }) => css`
  p {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: relative;
    flex-wrap: wrap;
    /* gap: 0.5rem; */

    ${Fonts.small}
    ${$isInView &&
    css`
      animation: ${ListStyleAnim} 0.5s linear;
      animation-fill-mode: both;
    `}

  &:nth-child(n) {
      ${Array.from({
        length: document.querySelector(".list-style")?.children.length || 0
      }).map(
        (_, index) => css`
          &:nth-child(${index + 1}) {
            animation-delay: ${0.1 + index * 0.1}s;
          }
        `
      )}
    }
    &:before {
      background-size: cover;
      content: "";
      width: 120px;
      height: 52px;
      display: block;
      background-image: url(/assets/leaf.PNG);
      background-repeat: no-repeat;
      background-position: center;
    }
  }
`;
export const PageContent = styled.div<{
  $isListStyle?: boolean;
  $isInView?: boolean;
}>`
  font-size: large;
  text-align: center;

  white-space: pre-line;
  ${Fonts.large}
  ${(p) => (p.$isListStyle ? listStyle({ $isInView: p.$isInView }) : "")}
`;

export const BannerWidthPic = styled(StyledImage)<{
  height?: CSSProperties["height"];
  $maxHeight?: CSSProperties["maxHeight"];
  width?: CSSProperties["width"];
}>`
  width: ${(p) => p.width ?? "100%"};
  height: ${(p) => p.height ?? "500px"};
  max-height: ${(p) => p.$maxHeight ?? undefined};
  object-fit: ${(p) => p.$objectFit ?? "cover"};
  margin: 0 auto;
  max-width: 1800px;
  display: flex;
`;
export const NextBannerWidthPic = styled(NextStyledImage)<{
  $maxHeight?: CSSProperties["maxHeight"];
}>`
  max-height: ${(p) => p.$maxHeight ?? undefined};
  object-fit: ${(p) => p.$objectFit ?? "cover"};
  margin: 0 auto;
  max-width: 1800px;
  display: flex;
`;

const StyledTitleFocusAnimation = keyframes`
  0%{

  }
  100%{
      transform: scale(1.05);
  }
`;

export const StyledTitle = styled.h1<{ $isInView?: boolean }>`
  ${Fonts.xLarge}
  font-weight: 700;
  text-align: center;

  ${(p) =>
    p.$isInView &&
    css`
      animation: ${StyledTitleFocusAnimation} forwards 1.5s;
    `}
`;

export const WhiteTzumiBox = styled.div<{
  $padding?: CSSProperties["padding"];
}>`
  background-color: ${defaultTheme.tertiaryColor};
  padding: ${(p) => p.$padding ?? "2rem"};
  border-radius: 20px;
  max-width: 100vw;
  * {
    color: ${defaultTheme.primaryColor};
  }
`;

export const CardWrap = styled.div<{
  $height?: CSSProperties["height"];
  $width?: CSSProperties["width"];
}>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  ${shadow}
  width:${(p) => p.$width ?? "300px"};
  height: ${(p) => p.$height ?? "min-content"};
  max-width: 500px;
  justify-content: space-between;
  align-items: center;
  /* border: 1px solid ${defaultTheme.primaryColor}; */
  padding: 1rem;
`;

export const ErrorText = styled.p`
  color: red;
`;

export const ProfileCard = styled.div<{
  $padding?: CSSProperties["padding"];
}>`
  width: 100%;
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  padding: ${(p) => p.$padding ?? "1.5rem"};
`;

export const GridContainer = styled.div<{
  $tableHead?: boolean;
  $border?: boolean;
  $width?: CSSProperties["width"];
  $coulumns?: number;
}>`
  display: grid;
  grid-template-columns: ${({ $coulumns }) =>
    `repeat(${$coulumns ?? 3}, 1fr)`}; /* Three columns */
  margin-top: 0.5rem;
  gap: 1rem; /* Gap between grid items */
  align-items: center;
  border: ${({ $border }) => ($border ? "1px solid black" : "none")};

  ${screens({
    desktop: css`
      width: 75%;
    `,
    mobile: css`
      width: 100%;
    `
  })}
  ${({ $width }) => ($width ? `width: ${$width} !important;` : "")}
  padding:.5rem;
  ${(p) =>
    p.$tableHead
      ? css`
          background-color: ${defaultTheme.primaryColor};
          ${Fonts.large}
          * {
            color: ${defaultTheme.tertiaryColor};
          }
        `
      : css`
          background-color: white;
        `}
`;

export const GridItem = styled.span`
  text-align: center;
  ${Fonts.medium}
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  /* Define styling for grid items */
`;

export const StyledIframe = styled.iframe`
  border: none;
  width: 80%;
  height: 80%;
  margin: auto;
`;
export const threeDots = css`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;
export const Line = styled.div`
  width: 100%;
  height: 2px;
  background-color: ${defaultTheme.primaryColor};
  margin: 2rem 0;
`;

export const Aside = styled.aside<{ $isMobile: boolean }>`
  width: ${(p) => (p.$isMobile ? "200px" : "30%")};
  max-width: 300px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  /* height: 100%; */
  background-color: white;
  height: min-content;
`;

export const CourseName = styled.h3`
  text-align: center;
  ${Fonts.large}
`;
export const Title = styled.p`
  ${Fonts.large}
`;
export const SubTitle = styled.p`
  ${Fonts.small}
  font-weight: normal;
`;

export const WarningText = styled(PageContent)`
  ${Fonts.medium}
  text-decoration: underline;
  color: red;
`;
